import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { branchApi } from "../Apis/Api";
import Breadcrumb from "../components/Breadcrumb";
import CustomSelect from "../components/common/CustomSelect";
import { handleApiResponse } from "../utils/response";

const SlotEditPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const slotData = location.state?.slotData;
  const slotId = slotData.id;
  const [formData, setFormData] = useState({
    location_id: "",
    slotNumber: "",
    slot_id: "",
    slot_type: "",
  });
  const [errors, setErrors] = useState({});
  const [slotsEditLoading, setSlotsEditLoading] = useState(false);

  const slotTypes = [
    { value: "open", label: "Open" },
    { value: "reserved", label: "Reserved" },
    { value: "occupied", label: "Occupied" },
  ];

  useEffect(() => {
    if (slotData) {
      setFormData({
        slot_id: slotData.slot_id,
        location_id: slotData.location_id?.toString() || "",
        slotNumber: slotData.slot_number || "",
        slot_type: slotData.slot_type || "open",
      });
    } else {
      const fetchSlotData = async () => {
        try {
          const response = await branchApi.getAllParkingSlots();
          const slot = response.data.find((s) => s.id === parseInt(slotId));
          if (slot) {
            setFormData({
              location_id: slot.location_id?.toString() || "",
              slotNumber: slot.slot_number || "",
              slot_type: slot.slot_type || "open",
            });
          } else {
            handleApiResponse(response, "Slot not found", "GET");
            navigate("/slots");
          }
        } catch (err) {
          handleApiResponse(err, "Failed to fetch slot data", "GET");

          navigate("/slots");
        }
      };

      if (slotId) fetchSlotData();
    }
  }, [slotId, slotData, navigate]);

  const handleInputChange = (selected, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selected ? selected.value : "",
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleTextInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.location_id) {
      newErrors.location_id = "Location is required";
    }
    if (!formData.slotNumber.trim()) {
      newErrors.slotNumber = "Slot number is required";
    }
    if (!formData.slot_type) {
      newErrors.slot_type = "Slot type is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setSlotsEditLoading(true);
      try {
        const res = await branchApi.updateParkingSlot(formData.slot_id, {
          location_id: parseInt(formData.location_id),
          slot_number: formData.slotNumber,
          slot_type: formData.slot_type,
        });
        handleApiResponse(
          res,
          `Slot "${formData.slotNumber}" updated successfully`,
          "PUT"
        );

        navigate("/slots");
      } catch (err) {
         handleApiResponse(err, `Failed to update slot`, "PUT");
      } finally {
        setSlotsEditLoading(false);
      }
    }
  };

  return (
    <>
      <Breadcrumb title="Slot Edit" />
      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24">
          <form onSubmit={handleSubmit}>
            <div className="row g-20">
              <div className="col-md-6 mb-20">
                <label
                  htmlFor="slotNumber"
                  className="form-label fw-semibold text-primary-light text-sm mb-8"
                >
                  Slot Number <span className="text-danger-600">*</span>
                </label>
                <input
                  type="text"
                  className="form-control radius-8"
                  id="slotNumber"
                  name="slotNumber"
                  value={formData.slotNumber}
                  onChange={handleTextInputChange}
                  placeholder="e.g., Slot A1"
                  readOnly
                  style={{
                    border: errors.slotNumber ? "1px solid #e53e3e" : "",
                  }}
                />
                {errors.slotNumber && (
                  <span className="text-sm text-danger-600 mt-2 d-block">
                    {errors.slotNumber}
                  </span>
                )}
              </div>
              <div className="col-md-6 mb-20">
                <label
                  htmlFor="slot_type"
                  className="form-label fw-semibold text-primary-light text-sm mb-8"
                >
                  Slot Type <span className="text-danger-600">*</span>
                </label>
                <CustomSelect
                  name="slot_type"
                  value={formData.slot_type}
                  onChange={(selected) =>
                    handleInputChange(selected, "slot_type")
                  }
                  options={slotTypes}
                  placeholder="Select Slot Type"
                  labelKey="label"
                  valueKey="value"
                  isSearchable={true}
                  isClearable={true}
                  style={{
                    border: errors.slot_type ? "1px solid #e53e3e" : "",
                  }}
                />
                {errors.slot_type && (
                  <span className="text-sm text-danger-600 mt-2 d-block">
                    {errors.slot_type}
                  </span>
                )}
              </div>
            </div>
            <div className="d-flex align-items-center justify-content-center gap-3">
              <Link
                to="/slots"
                className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
              >
                Cancel
              </Link>
              <button
                type="submit"
                className="btn btn-primary-600 border border-primary-600 text-md px-56 py-12 radius-8"
                disabled={slotsEditLoading}
              >
                {slotsEditLoading ? (
                  <>
                    <div
                      style={{
                        border: "2px solid rgba(255, 255, 255, 0.3)",
                        borderRadius: "50%",
                        borderTopColor: "#fff",
                        width: "16px",
                        height: "16px",
                        animation: "spin 1s ease-in-out infinite",
                        display: "inline-block",
                        marginRight: "8px",
                      }}
                    />
                    Processing...
                  </>
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default SlotEditPage;
